import {
  Icon,
  InlineMessage,
  InputField,
  Typography,
} from "@saviynt/design-system";
import { ComponentType, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { combineFieldStyles, fieldStyles } from "../components/FieldStyles";
import { FieldWrapper } from "../components/FieldWrapper";
import { useTheme } from "@repo/mfe-core";
import { useUI } from "../contexts/UIContext";

export interface FieldProps {
  id: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  rows?: number;
  isReadOnly?: boolean;
  value?: string;
  defaultValue?: string;
  // Allow custom styling override while maintaining base styles
  className?: string;
  regexPattern?: string;
  validationMessage?: string;
}

// Validation helpers
const validateAWSAccountId = (value: string): boolean => {
  // AWS Account ID must be exactly 12 digits
  return /^\d{12}$/.test(value);
};

const validateRoleARN = (value: string): boolean => {
  // AWS Role ARN pattern: arn:aws:iam::account-id:role/role-name
  return /^arn:aws:iam::\d{12}:role\/[a-zA-Z0-9+=,.@_-]+$/.test(value);
};

// Password Input Component - Uses consistent styling with visibility toggle
function PasswordInput({
  id,
  label,
  placeholder,
  required,
  isReadOnly,
  value,
  className,
  validationMessage,
}: FieldProps) {
  const { getFieldValue, setFieldValue } = useUI();
  const { theme: currentTheme } = useTheme();
  const intl = useIntl();
  const fieldValue = isReadOnly ? value || "" : getFieldValue(id);
  const [isCritical, setIsCritical] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [visible, setVisible] = useState(false);

  const validateField = (val: string): boolean => {
    const trimmedVal = val.trim();
    // Just check required/empty for password fields
    if (!required && !trimmedVal) return true; // Optional fields are valid when empty
    if (required && !trimmedVal) return false; // Required fields must have non-empty value
    return true; // Has value means valid
  };

  // Re-validate when fieldValue changes (for external updates)
  useEffect(() => {
    if (hasBlurred || fieldValue.length > 0) {
      const isValid = validateField(fieldValue);
      setIsCritical(!isValid);
    }
  }, [fieldValue, hasBlurred, required]);

  const handleValueChange = (val: string) => {
    setFieldValue(id, val);
    const isValid = validateField(val);
    if (hasBlurred || val.length > 0) {
      setIsCritical(!isValid);
    } else {
      setIsCritical(false);
    }
  };

  // Track when user leaves the field to enable validation
  const handleBlur = () => {
    setHasBlurred(true);
    const currentValue = isReadOnly ? value || "" : getFieldValue(id);
    const isValid = validateField(currentValue);
    setIsCritical(!isValid);
  };

  // Get default validation message
  const getDefaultValidationMessage = () => {
    return (
      validationMessage ||
      intl.formatMessage({
        id: "c6SeZ5",
        defaultMessage: "Please enter a valid value",
        description: "Generic field validation error",
      })
    );
  };

  return (
    <div className={className} onBlur={handleBlur}>
      <InputField
        name={id}
        label={
          <Typography kind="label" htmlFor={id}>
            {label}
          </Typography>
        }
        placeholder={placeholder || ""}
        type={visible ? "text" : "password"}
        value={fieldValue}
        setValue={handleValueChange}
        isRequired={required}
        isReadOnly={isReadOnly}
        isCritical={isCritical}
        setIsCritical={setIsCritical}
        CriticalHelperText={
          isCritical ? (
            <InlineMessage
              text={getDefaultValidationMessage()}
              colorTheme="critical"
              size="small"
              leftIcon={<Icon kind="AlertCriticalFilled" />}
            />
          ) : undefined
        }
        suffixButtonType="password"
        suffixIsToggle={visible}
        suffixOnClick={() => setVisible(!visible)}
        dataTheme={currentTheme === "dark" ? "dark" : "light"}
      />
    </div>
  );
}

// Text Input Component - Uses consistent styling
// InputField handles its own label, so we don't wrap it
function TextInput({
  id,
  label,
  placeholder,
  required,
  isReadOnly,
  value,
  className,
  regexPattern,
  validationMessage,
}: FieldProps) {
  const { getFieldValue, setFieldValue } = useUI();
  const { theme: currentTheme } = useTheme();
  const intl = useIntl();
  const fieldValue = isReadOnly && value ? value : getFieldValue(id);
  const [isCritical, setIsCritical] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  // Determine validation function based on field ID
  const getValidationFunction = () => {
    if (id === "account_id") {
      return validateAWSAccountId;
    }
    if (id === "role_arn") {
      return validateRoleARN;
    }
    if (regexPattern) {
      return (val: string) => new RegExp(regexPattern).test(val);
    }
    return null;
  };

  const validateField = (val: string): boolean => {
    const trimmedVal = val.trim();

    // Character limit validation for name field
    if (id === "name" && val.length > 255) {
      return false;
    }

    // Get validator first to check if field has specific validation rules
    const validator = getValidationFunction();

    // If field has a specific validator (like account_id or role_arn), use it
    if (validator) {
      // For required fields with validators, empty value should be invalid
      if (required && !trimmedVal) {
        return false;
      }
      // If field has value, validate it with the validator
      if (trimmedVal) {
        return validator(trimmedVal);
      }
      // If not required and empty, it's valid
      if (!required && !trimmedVal) {
        return true;
      }
      // Required but empty - invalid
      return false;
    }

    // No specific validator - just check required/empty
    if (!required && !trimmedVal) return true; // Optional fields are valid when empty
    if (required && !trimmedVal) return false; // Required fields must have non-empty value
    return true; // Has value and no specific validator means valid
  };

  // Re-validate when fieldValue changes (for external updates)
  useEffect(() => {
    if (hasBlurred || fieldValue.length > 0) {
      const isValid = validateField(fieldValue);
      setIsCritical(!isValid);
    }
  }, [fieldValue, hasBlurred, id, required, regexPattern]);

  const handleValueChange = (val: string) => {
    setFieldValue(id, val);
    const isValid = validateField(val);

    // For account_id and role_arn, show validation immediately as user types
    // For other fields, show validation after blur or if field has value
    if (id === "account_id" || id === "role_arn") {
      setIsCritical(!isValid);
    } else if (hasBlurred || val.length > 0) {
      setIsCritical(!isValid);
    } else {
      setIsCritical(false);
    }
  };

  // Track when user leaves the field to enable validation
  const handleBlur = () => {
    setHasBlurred(true);
    const currentValue = isReadOnly ? value || "" : getFieldValue(id);
    const isValid = validateField(currentValue);
    setIsCritical(!isValid);
  };

  // Get default validation message based on field type
  const getDefaultValidationMessage = () => {
    if (id === "name" && fieldValue.length > 255) {
      return intl.formatMessage({
        id: "P23j8n",
        defaultMessage: "Integration name cannot exceed 255 characters",
        description: "Integration name character limit error",
      });
    }
    if (id === "account_id") {
      return intl.formatMessage({
        id: "k/Jrmi",
        defaultMessage: "AWS Account ID must be exactly 12 digits",
        description: "AWS Account ID validation error",
      });
    }
    if (id === "role_arn") {
      return intl.formatMessage({
        id: "KAyUGQ",
        defaultMessage: "Role ARN must be a valid AWS IAM role ARN",
        description: "Role ARN validation error",
      });
    }
    return (
      validationMessage ||
      intl.formatMessage({
        id: "c6SeZ5",
        defaultMessage: "Please enter a valid value",
        description: "Generic field validation error",
      })
    );
  };

  return (
    <div className={className} onBlur={handleBlur}>
      <InputField
        name={id}
        label={
          <Typography kind="label" htmlFor={id}>
            {label}
          </Typography>
        }
        placeholder={placeholder || ""}
        value={fieldValue}
        setValue={handleValueChange}
        isRequired={required}
        isReadOnly={isReadOnly}
        isCritical={isCritical}
        setIsCritical={setIsCritical}
        CriticalHelperText={
          isCritical ? (
            <InlineMessage
              text={getDefaultValidationMessage()}
              colorTheme="critical"
              size="small"
              leftIcon={<Icon kind="AlertCriticalFilled" />}
            />
          ) : undefined
        }
        dataTheme={currentTheme === "dark" ? "dark" : "light"}
      />
    </div>
  );
}

// Text Area Component - Uses consistent styling
function TextArea({
  id,
  label,
  placeholder,
  defaultValue,
  className,
  required,
}: FieldProps) {
  const { getFieldValue, setFieldValue } = useUI();
  const { theme: currentTheme } = useTheme();
  const intl = useIntl();
  const fieldValue = getFieldValue(id) || defaultValue || "";
  const [isCritical, setIsCritical] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  const validateField = (val: string): boolean => {
    const trimmedVal = val.trim();

    // Character limit validation for description field
    if (id === "description" && val.length > 1000) {
      return false;
    }

    // Check required/empty
    if (!required && !trimmedVal) return true;
    if (required && !trimmedVal) return false;
    return true;
  };

  useEffect(() => {
    if (hasBlurred || fieldValue.length > 0) {
      const isValid = validateField(fieldValue);
      setIsCritical(!isValid);
    }
  }, [fieldValue, hasBlurred, required]);

  const handleValueChange = (val: string) => {
    setFieldValue(id, val);
    const isValid = validateField(val);
    if (hasBlurred || val.length > 0) {
      setIsCritical(!isValid);
    } else {
      setIsCritical(false);
    }
  };

  const handleBlur = () => {
    setHasBlurred(true);
    const currentValue = getFieldValue(id) || defaultValue || "";
    const isValid = validateField(currentValue);
    setIsCritical(!isValid);
  };

  const getValidationMessage = () => {
    if (id === "description" && fieldValue.length > 1000) {
      return intl.formatMessage({
        id: "7aSYG3",
        defaultMessage: "Integration description cannot exceed 1000 characters",
        description: "Integration description character limit error",
      });
    }
    return intl.formatMessage({
      id: "c6SeZ5",
      defaultMessage: "Please enter a valid value",
      description: "Generic field validation error",
    });
  };

  return (
    <div className={className} onBlur={handleBlur}>
      <InputField
        name={id}
        placeholder={placeholder || ""}
        label={
          <Typography kind="label" htmlFor={id}>
            {label}
          </Typography>
        }
        value={fieldValue}
        setValue={handleValueChange}
        kind="multiline"
        isRequired={required}
        isCritical={isCritical}
        setIsCritical={setIsCritical}
        CriticalHelperText={
          isCritical ? (
            <InlineMessage
              text={getValidationMessage()}
              colorTheme="critical"
              size="small"
              leftIcon={<Icon kind="AlertCriticalFilled" />}
            />
          ) : undefined
        }
        dataTheme={currentTheme === "dark" ? "dark" : "light"}
      />
    </div>
  );
}

/**
 * Generic field renderer for unknown field types from backend
 * Automatically applies consistent styling without code changes
 */
function GenericField({
  id,
  label,
  placeholder,
  required,
  className,
  value,
  defaultValue,
}: FieldProps) {
  const { getFieldValue, setFieldValue } = useUI();
  const fieldValue = getFieldValue(id) || defaultValue || value || "";

  return (
    <FieldWrapper label={label} required={required} className={className}>
      <input
        type="text"
        name={id}
        value={fieldValue}
        onChange={(e) => setFieldValue(id, e.target.value)}
        placeholder={placeholder}
        className={combineFieldStyles(
          fieldStyles.input.base,
          fieldStyles.input.padding,
          fieldStyles.input.focus,
          className,
        )}
      />
    </FieldWrapper>
  );
}

// Component Registry - Generic field components only
// New field types from backend will automatically use GenericField with consistent styling
export const componentRegistry: Record<string, ComponentType<FieldProps>> = {
  text_input: TextInput,
  password_input: PasswordInput,
  text_area: TextArea,
  // Fallback for unknown field types - ensures consistent styling
  default: GenericField,
};

/**
 * Get component for field type, falls back to GenericField for unknown types
 * This allows backend to send new field types without UI code changes
 */
export function getFieldComponent(
  fieldType: string,
): ComponentType<FieldProps> {
  return (
    componentRegistry[fieldType] || componentRegistry.default || GenericField
  );
}
